import React from 'react';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import styledContainerQuery from 'styled-container-query';
import tinycolor from 'tinycolor2';

import { DposLoyaltyDefault as DposLoyaltyIcon } from 'src/assets/dpos-loyalty-default';
import usePaths from 'src/hooks/use-paths';
import type { DutchiePosLoyaltyConfig, RewardsListV2 } from 'src/gql/graphql';
import { LoyaltyPassFooter } from 'src/components/loyalty/loyalty-pass/footer';
import { FooterContainer } from 'src/components/loyalty/loyalty-pass/footer.styles';
import { VerifyPhoneToRedeemNotice, WidgetGroup } from 'src/components/loyalty/verify-phone-to-redeem-notice';
import { useLoyaltyRedemptionPhoneVerification } from 'src/components/modals/loyalty-redemption-phone-verification';
import { useLoyalty } from '../loyalty-tiers/use-loyalty';

type LoyaltyProps = {
  isWidgetLink?: boolean;
  locationMargin?: string;
  displayCTA?: boolean;
  activeEnterpriseId?: string;
  loyaltyEnterpriseConfigData?: DutchiePosLoyaltyConfig;
  rewardsEnterpriseData?: RewardsListV2;
};

export const DiscountRedemption = ({
  isWidgetLink = true,
  locationMargin = '',
  displayCTA = true,
  activeEnterpriseId = undefined,
  loyaltyEnterpriseConfigData = undefined,
  rewardsEnterpriseData = undefined,
}: LoyaltyProps): JSX.Element | null => {
  const verifyPhone = useLoyaltyRedemptionPhoneVerification(activeEnterpriseId);
  const {
    formatValueForDisplay,
    formatExpirationDate,
    rewardsProgramName,
    balanceDaysToExpiration,
    progressPercentageToNextReward,
    targetRewardsBalance,
    progressToNextReward,
    currentRewardsBalance,
    loyaltyWidgetEnabled,
    formatValueForDaysForExpire,
    getExpiresVerb,
    getExpiryUnit,
    soonestExpiringBalance,
    soonestExpirationDate,
    isDiscountRedemption,
    redemptionRequiresPhoneVerification,
  } = useLoyalty(activeEnterpriseId, loyaltyEnterpriseConfigData, rewardsEnterpriseData);
  const { href: baseHref } = usePaths();

  if (!loyaltyWidgetEnabled) {
    return null;
  }

  const url = `${baseHref}/user/loyalty`;
  const showSoonestExpiry = soonestExpiringBalance != null && !!soonestExpirationDate;

  return (
    <WidgetGroup $locationMargin={locationMargin}>
      <Wrapper
        $displayCTA={displayCTA}
        href={isWidgetLink ? url : null}
        as={isWidgetLink ? Link : 'div'}
        $locationMargin='0'
      >
        <DispoInfo>
          <Image>
            <DposLoyaltyIcon />
          </Image>
          <div>
            <Name>{rewardsProgramName}</Name>
          </div>
        </DispoInfo>
        <MeterLabel>
          <RewardHeaderText>{formatValueForDisplay(currentRewardsBalance, 'points', true)}</RewardHeaderText>
          {showSoonestExpiry && (
            <RewardExpire>
              {formatValueForDisplay(
                soonestExpiringBalance,
                getExpiryUnit(soonestExpiringBalance),
                isDiscountRedemption
              )}{' '}
              {getExpiresVerb(soonestExpiringBalance)} {formatExpirationDate(soonestExpirationDate)}
            </RewardExpire>
          )}
          {!showSoonestExpiry && currentRewardsBalance > 0 && (
            <RewardExpire>{formatValueForDaysForExpire(balanceDaysToExpiration ?? 0)}</RewardExpire>
          )}
        </MeterLabel>

        {targetRewardsBalance ? (
          <>
            <Meter
              role='meter'
              aria-valuemin={0}
              aria-valuemax={targetRewardsBalance}
              aria-valuenow={currentRewardsBalance}
              aria-labelledby={`${rewardsProgramName} progress`}
            >
              <MeterLabelHeader $displayCTA={displayCTA}>
                {formatValueForDisplay(progressToNextReward, '', true)} more points to unlock next reward
              </MeterLabelHeader>
              <MeterProgress $width={progressPercentageToNextReward} />
              {displayCTA && <DiscountCTA>View Rewards</DiscountCTA>}
            </Meter>
          </>
        ) : null}

        <LoyaltyPassFooter
          borderOverrides={css`
            left: -20px;
            right: -20px;
          `}
        />
      </Wrapper>
      {redemptionRequiresPhoneVerification && <VerifyPhoneToRedeemNotice onVerify={() => void verifyPhone()} />}
    </WidgetGroup>
  );
};

const DispoInfo = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`;

const Image = styled.div`
  border-radius: 50%;
  color: var(--dct--customized-colors-buttonsLinks);
  display: block;
  height: 56px;
  overflow: hidden;
  width: 56px;
`;

const Name = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: ${18 / 16};
`;

const Meter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin: 32px 0 20px;
`;

const MeterLabelHeader = styled.div`
  display: flex;
  width: 160px;
`;

const RewardHeaderText = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
`;

const MeterLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
`;

const MeterProgress = styled.div<{ $width: string }>`
  background: linear-gradient(
    90deg,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()} 0%,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()}
      ${({ $width }) => $width},
    #fff ${({ $width }) => $width}
  );
  border-radius: var(--dct--customized-radius-sm);
  height: 8px;
  order: -1;
  position: relative;
  width: 100%;
`;

const DiscountCTA = styled.div`
  align-items: center;
  width: 130px;
  background: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()};
  border-radius: 20px;
  display: flex;
  gap: 12px;
  justify-content: center;
  line-height: 1;
  padding: 8px 8px;
`;

const RewardExpire = styled.div`
  font-size: 12px;
  font-weight: 400;
  text-align: end;
  margin-top: 4px;
`;

// This is our container query component
const Wrapper = styledContainerQuery.a`
  background: var(--dct--customized-colors-buttonsLinks);
  border-radius: var(--dct--customized-radius-lg);
  color: #fff;
  display: block;
  margin: ${({ $locationMargin }) => $locationMargin || '20px auto 40px'};
  max-width: 1200px;
  overflow: hidden;
  padding: 20px;
  position: relative;
  width: 100%;
  z-index: 1;

  /* In block mode, add margin-top to footer for spacing */
  ${FooterContainer} {
    margin-top: 20px;
  }

  &::before {
    background: #ffffff1a;
    border-radius: 50%;
    content: '';
    height: 400px;
    left: 48px;
    pointer-events: none;
    position: absolute;
    top: 48px;
    transform: translate(-50%, -50%);
    width: 400px;
    z-index: -1;
  }
  &:container(min-width: 300px and max-width: 599px) {
    ${MeterLabelHeader} {
      display: flex;
      width: ${({ $displayCTA }) => ($displayCTA ? '160px !important' : '100%')};
      justify-content: center;
    }

    ${RewardHeaderText} {
        font-size: 24px;
        font-weight: 700;
        line-height: 1;
        display: inline-flex;
        margin-top: 24px;
    }
    ${RewardExpire} {
        font-size: 12px;
        font-weight: 400;
        align-content: end;
        line-height: 16px;
        margin-left: 6px;
        display: inline-flex;
    }

    ${Meter} {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      justify-content: space-between;
      margin: 24px 0 0px;
    }
  }

  &:container(min-width: 600px) {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: space-between;

    /* In flex mode, remove margin-top from footer since gap provides spacing */
    ${FooterContainer} {
      margin-top: 0;
    }

    ${Meter} {
      align-items: center;
      gap: 16px;
      margin: 0;
      order: 3;
      width: 100%;
    }
    ${MeterProgress} {
      flex: 1;
      order: unset;
      width: auto;
    }
    ${DiscountCTA} {
      width: 130px;
    }
  }
  &:container(min-width: 900px) {
    gap: 32px;
    justify-content: flex-start;
    ${DispoInfo} {
      max-width: 218px;
    }
    ${Meter} {
      flex: 1;
      order: unset;
      width: auto;
    }
    ${RewardExpire} {
      font-size: 12px;
      font-weight: 400;
      text-align: start;
      margin-top: 4px;
    }
  }
`;
